import { formatBsDate, KATHMANDU_TZ } from '@/lib/bsDate'

/** Past this many days a story shows its date, not "8d ago". */
export const RELATIVE_AGE_DAYS = 7

const KATHMANDU_DAY = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: KATHMANDU_TZ,
})

const WEEKDAY = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'UTC' })

const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

/** `YYYY-MM-DD` with no time part: mm-be's `published_at` on `/market/updates/` today. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

/** A Kathmandu calendar day as a UTC day number, so two of them subtract to whole days. */
const dayNumber = (value: Date | string): number | null => {
  const text = typeof value === 'string' ? value : KATHMANDU_DAY.format(value)
  const day = DATE_ONLY.test(text)
    ? text
    : Number.isNaN(new Date(text).getTime())
      ? null
      : KATHMANDU_DAY.format(new Date(text))
  if (day === null) return null
  return Math.floor(Date.parse(`${day}T00:00:00Z`) / DAY_MS)
}

/**
 * Whole Kathmandu calendar days from `now` to `date` (`YYYY-MM-DD` or a timestamp): 0 today,
 * 1 tomorrow, −1 yesterday. Null when `date` is unreadable.
 */
export const daysFromToday = (date: string, now: Date = new Date()): number | null => {
  const day = dayNumber(date)
  const today = dayNumber(now)
  return day === null || today === null ? null : day - today
}

/**
 * How long ago a story was published, in the words Home's news rows use: "5m ago", "2h ago",
 * "1d ago" … "7d ago", then the BS date ("1 Bhadra 2083"). Today's "Today" when the wire gives only
 * a day, since "hours ago" cannot be known from it; a stamp with a time gets minutes and hours.
 * A date in the future (a clock that disagrees) is read as today rather than shown as "-1d ago".
 * An unreadable value is "—", as every date in the app is.
 */
export const publishedAge = (publishedAt: string, now: Date = new Date()): string => {
  const days = dayNumber(publishedAt)
  const today = dayNumber(now)
  if (days === null || today === null) return '—'
  const ago = Math.max(0, today - days)
  if (ago > RELATIVE_AGE_DAYS) return formatBsDate(publishedAt)
  if (!DATE_ONLY.test(publishedAt) && ago === 0) {
    const elapsed = Math.max(0, now.getTime() - new Date(publishedAt).getTime())
    if (elapsed < HOUR_MS) return `${String(Math.max(1, Math.floor(elapsed / MINUTE_MS)))}m ago`
    return `${String(Math.floor(elapsed / HOUR_MS))}h ago`
  }
  return ago === 0 ? 'Today' : `${String(ago)}d ago`
}

/**
 * When a calendar notice falls, the way the mock's announcements read: "Today", "Tomorrow",
 * "Yesterday", the weekday inside the week either side ("Fri"), and otherwise the date label the
 * caller already holds.
 */
export const eventWhen = (date: string, dateLabel: string, now: Date = new Date()): string => {
  const day = dayNumber(date)
  const away = daysFromToday(date, now)
  if (day === null || away === null) return dateLabel
  if (away === 0) return 'Today'
  if (away === 1) return 'Tomorrow'
  if (away === -1) return 'Yesterday'
  if (Math.abs(away) < 7) return WEEKDAY.format(new Date(day * DAY_MS))
  return dateLabel
}

/**
 * "2 days ago", "1 week ago", "1 month ago": a post's age in whole words, one unit, the way the
 * mock's "Latest from MoneyMitra" cards read. Under a day is "Today"; a date in the future is read
 * as today; an unreadable value is "—".
 */
export const publishedAgeLong = (publishedAt: string, now: Date = new Date()): string => {
  const days = dayNumber(publishedAt)
  const today = dayNumber(now)
  if (days === null || today === null) return '—'
  const ago = Math.max(0, today - days)
  const unit = (count: number, word: string) =>
    `${String(count)} ${word}${count === 1 ? '' : 's'} ago`
  if (ago === 0) return 'Today'
  if (ago < 7) return unit(ago, 'day')
  if (ago < 30) return unit(Math.floor(ago / 7), 'week')
  if (ago < 365) return unit(Math.floor(ago / 30), 'month')
  return unit(Math.floor(ago / 365), 'year')
}
